import { useEffect, useState, useCallback } from 'react';

// Shared global store for the PWA install prompt.
// The browser fires `beforeinstallprompt` only once, so we capture the event
// at module scope and share it across every component that needs it
// (floating banner + navbar settings menu).
let deferredPrompt = null;

const detectStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

let isInstalled = detectStandalone();
const listeners = new Set();

const notify = () => {
  listeners.forEach((listener) => listener());
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    isInstalled = true;
    notify();
  });
}

const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
const isAndroid = /Android/.test(ua);
// iOS "Add to Home Screen" only works from Safari, not Chrome/Firefox on iOS.
const isIOSSafari = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);

/**
 * Hook that exposes whether the app can be installed and helpers to install it.
 *
 * @returns {{
 *   canInstall: boolean,        // native prompt available (Chrome/Edge/Android)
 *   canPromptNatively: boolean, // alias for canInstall
 *   isInstalled: boolean,
 *   isIOS: boolean,
 *   isIOSSafari: boolean,
 *   isAndroid: boolean,
 *   promptInstall: () => Promise<'accepted'|'dismissed'|null>
 * }}
 */
export default function usePWAInstall() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    listeners.add(listener);
    // Re-check installed state when returning to the app.
    if (detectStandalone()) {
      isInstalled = true;
    }
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return null;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      isInstalled = true;
    }
    deferredPrompt = null;
    notify();
    return outcome;
  }, []);

  const canPromptNatively = Boolean(deferredPrompt) && !isInstalled;

  return {
    canInstall: canPromptNatively,
    canPromptNatively,
    isInstalled,
    isIOS,
    isIOSSafari,
    isAndroid,
    promptInstall,
  };
}
